import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { Button } from "baseui/button";
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid';
import { Check, Delete, Search } from "baseui/icon";
import { Input } from "baseui/input";
import { Cell, Grid } from "baseui/layout-grid";
import { DURATION, useSnackbar } from "baseui/snackbar";
import { Tab, Tabs } from 'baseui/tabs-motion';
import { KIND, Tag, VARIANT } from "baseui/tag";
import { HeadingXLarge, LabelSmall } from 'baseui/typography';
import { debounce } from "lodash";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { loadChallengeFeed } from "../../application/actions/challenges";
import { buyLicenseForChallenge } from "../../application/actions/licenses";
import { loadSolutionById, loadSolutions, loadSolutionsForChallenge, searchSolutions } from "../../application/actions/solutions";
import { routeTo } from "../../application/actions/ui";
import { getWallet } from '../../application/selectors/wallet';
import ReDoBanner from "../../components/ReDoBanner";
import ReDoButton from "../../components/ReDoButton";
import ReDoCheckbox from "../../components/ReDoCheckbox";
import ReDoProjectCard from '../../components/ReDoProjectCard';
import ReDoSpinner from "../../components/ReDoSpinner";
import useBreakpoint from "../../hooks/useBreakpoint";
import useWhoAmI from "../../hooks/useWhoAmI";
import useLocale from "../../hooks/useLocale";

const NAVBAR_HEIGHT = 68;
const CARD_HEIGHT = '550px';


const filters = [
  {
    areas: [
      'Energy',
      'Water',
      'Waste',
      'Procurement',
      'Materials',
    ]
  },
  {
    areas: [
      'Communication',
      'Supply Chains',
      'Packaging',
      'End of Life',
      'Infrastructure',
      'Climate Adaption',
    ]
  },
  { voteCount: ['High to low', 'Low to high'] },
  { stages: ['Early stage', 'Mid stage', 'Final stage'] },
]

const filterNames = {
  areas: 'Area',
  voteCount: 'Votes',
  stages: 'Stage',
}

const isChallengeSolution = (solution, challengeTitles = new Set()) => !!(
  solution?.challengeId ||
  solution?.challengeID ||
  solution?.challenge_id ||
  solution?.challenge ||
  solution?.isChallengeSolution ||
  solution?.projectType === 'challengeSolution' ||
  solution?.visibility === 'company' ||
  challengeTitles.has(solution?.what) ||
  challengeTitles.has(solution?.challengeTitle)
);

const SolutionsOverview = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [css, theme] = useStyletron();

  const wallet = useSelector(getWallet)

  const [solutions, setSolutions] = useState([])
  const [loading, setLoading] = useState()
  const [chosenFilters, setChosenFilters] = useState({})
  const [searchTerm, setSearchTerm] = useState()
  const [searchResults, setSearchResults] = useState()
  const [activeKey, setActiveKey] = useState(0);

  const { enqueue } = useSnackbar();

  const breakpoint = useBreakpoint()
  const { isCompany } = useWhoAmI()
  const { t } = useLocale()

  const filterNamesTranslated = {
    areas: t('solutions.filter_area'),
    voteCount: t('solutions.filter_votes'),
    stages: t('solutions.filter_stage'),
  }

  const getVisibleSolutions = useCallback(async (data = []) => {
    if (isCompany) return data;

    let challengeTitles = new Set();
    let challengeSolutionIds = new Set();

    try {
      const challenges = await dispatch(loadChallengeFeed);
      challengeTitles = new Set(challenges.map(challenge => challenge.title).filter(Boolean));
      const challengeSolutionResponses = await Promise.allSettled(
        challenges.map(challenge => dispatch(loadSolutionsForChallenge(challenge.id)))
      );
      challengeSolutionIds = new Set(
        challengeSolutionResponses
          .filter(result => result.status === 'fulfilled')
          .flatMap(result => result.value || [])
          .map(solution => solution.id)
      );
    } catch (e) { }

    return data.filter(solution =>
      !challengeSolutionIds.has(solution.id) && !isChallengeSolution(solution, challengeTitles)
    );
  }, [dispatch, isCompany])

  const filteredSolutions = useMemo(() => {
    const hasOtherFilters = searchTerm || Object.entries(chosenFilters).some(([k, v]) => k !== 'voteCount' && v?.length);
    const data = hasOtherFilters ? searchResults : solutions;

    const voteSort = chosenFilters?.voteCount?.[0];
    if (!voteSort || !data) return data;
    return [...data].sort((a, b) =>
      voteSort === 'high'
        ? (b.voteCount || 0) - (a.voteCount || 0)
        : (a.voteCount || 0) - (b.voteCount || 0)
    );
  }, [solutions, searchTerm, searchResults, chosenFilters])

  const debouncedSearch = useMemo(() =>
    debounce(async (criteria, filters) => {
      setSearchTerm(criteria)
      const { voteCount: _voteCount, ...apiFilters } = filters
      const payload = { term: criteria, ...apiFilters }
      const response = await dispatch(searchSolutions(payload))

      setSearchResults(await getVisibleSolutions(response))
    }, 300)
    , [dispatch, getVisibleSolutions]);

  const onSearch = (e) => {
    debouncedSearch(e.target.value, chosenFilters);
  }

  const onFavorite = () => {
    setChosenFilters({})
    setSearchTerm('')
    setSearchResults([])
    dispatch(loadSolutions).then((data) => {
      getVisibleSolutions(data).then(setSolutions)
      setLoading(false)
    })
  }

  const resetFilters = () => {
    setChosenFilters({})
    debouncedSearch(searchTerm, {});
  }

  const onFilter = useCallback((e, synthetic) => {
    const checkedFilterName = e?.target?.labels?.[0]?.outerText || synthetic?.checkedFilterName

    let checkedFilterGroup
    filters.some(filterObj => {
      const filterKey = Object.keys(filterObj)[0];
      if (filterObj[filterKey].includes(checkedFilterName)) {
        checkedFilterGroup = filterKey;
        return true
      }
      return false
    })

    let filterPayload
    const checkedFilterAPIName = (checkedFilterGroup === 'areas' ? checkedFilterName.toLowerCase() : e?.target?.labels?.[0]?.outerText?.toLowerCase()?.split(' ')?.[0]) || synthetic?.checkedFilterAPIName
    const checkedFilterValue = e?.target?.checked || synthetic?.checkedFilterValue


    if (checkedFilterValue) {
      filterPayload = { ...chosenFilters, [checkedFilterGroup]: [...(chosenFilters?.[checkedFilterGroup] || []), checkedFilterAPIName] }
      setChosenFilters(filterPayload)
    } else {
      filterPayload = { ...chosenFilters, [checkedFilterGroup]: chosenFilters?.[checkedFilterGroup]?.filter(filter => filter !== checkedFilterAPIName) }
      setChosenFilters(filterPayload)
    }
    debouncedSearch(searchTerm, filterPayload);
  }, [chosenFilters, debouncedSearch, searchTerm])

  const navigateToSolution = (solution) => {
    if (solution.purchased) {
      dispatch(loadSolutionById(solution.id)).then(sol => {
        dispatch(routeTo({ view: 'solution', payload: { solution: sol } }))
      })
    } else {
      setLoading(true)
      dispatch(buyLicenseForChallenge({ solutionId: solution.id })).then(() => {
        enqueue({ message: t('solutions.toast_purchase_success'), dismissAfter: DURATION.shortest, startEnhancer: () => <Check size={theme.sizing.scale800} /> })
        setChosenFilters({})
        setSearchTerm('')
        setSearchResults([])
        dispatch(loadSolutions).then((data) => {
          getVisibleSolutions(data).then(setSolutions)
          setLoading(false)
        })
      }).catch(e => {
        setLoading(false)
        enqueue({ message: e?.response?.data?.error, dismissAfter: DURATION.shortest, startEnhancer: () => <Delete size={theme.sizing.scale800} /> })
      })
    }
  }

  const getIsChecked = useCallback((filterName, filterValue) => {
    return chosenFilters?.[filterName]?.includes(filterValue?.toLowerCase()?.split(' ')?.[0]) || chosenFilters?.[filterName]?.includes(filterValue.toLowerCase())
  }, [chosenFilters])

  const getChosenFilterFullName = (filterName, filterValue) => {
    return filters?.find(filter => filter[filterName])?.[filterName]?.find(filter => filter?.toLowerCase()?.split(' ')?.[0] === filterValue) || filterValue
  }

  useEffect(() => {
    setLoading(true)
    dispatch(loadSolutions).then((data) => {
      getVisibleSolutions(data).then(setSolutions)
      setLoading(false)
    })
    return () => {
      debouncedSearch.cancel();
    };
  }, [dispatch, debouncedSearch, getVisibleSolutions])

  return (
    <>

      {loading ? <ReDoSpinner /> :
        <FlexGrid
          flexGridColumnCount={[1, 1, 1, 2]}
          flexGridRowGap="scale800"
          height='100%'
          alignItems='center'
          overflow={breakpoint === 'large' ? 'hidden' : 'unset'}
          margin={breakpoint === 'large' ? '0 -28px' : 'unset'}
        >
          <FlexGridItem position='relative' height='100%' display={['none', 'none', 'none', 'flex']}>
            <ReDoBanner>
              <div className={css({ height: '100%', display: 'flex', flexDirection: 'column', padding: `${theme.sizing.scale900} 6ch` })}>
                <HeadingXLarge marginTop='2ch' overrides={{ Block: { style: { fontWeight: '300', lineHeight: '48px' } } }}>{t('solutions.banner_heading')}</HeadingXLarge>
                <Block marginTop='7%' height={theme.sizing.scale1400}>
                  <Input
                    onChange={onSearch}
                    startEnhancer={<Search size="24px" />}
                    placeholder={t('solutions.input_search')}
                    overrides={{
                      Root: {
                        style: {
                          height: '100%',
                          borderRadius: '50px',
                          backgroundColor: 'transparent',
                          borderColor: 'transparent',
                          boxShadow: `0 0 15px 0px rgba(94, 94, 91, 20%)`
                        }
                      },
                      StartEnhancer: {
                        style: {
                          backgroundColor: 'transparent',
                        }
                      },
                      InputContainer: {
                        style: {
                          backgroundColor: 'transparent',
                        }
                      },
                    }}
                  />
                </Block>
                <Block paddingBottom='1ch' overrides={{ Block: { style: { borderBottom: `1px solid ${theme.colors.primary100}` } } }} marginTop='7%'>
                  <Grid gridColumns={4} gridMargins={4}>
                    <Cell >
                      <LabelSmall overrides={{ Block: { style: { fontWeight: 'bold' } } }}>{t('solutions.filter_area')}</LabelSmall>
                    </Cell>
                    <Cell >
                      <LabelSmall overrides={{ Block: { style: { fontWeight: 'bold' } } }}></LabelSmall>
                    </Cell>
                    <Cell >
                      <LabelSmall overrides={{ Block: { style: { fontWeight: 'bold' } } }}>{t('solutions.filter_votes')}</LabelSmall>
                    </Cell>
                    <Cell >
                      <LabelSmall overrides={{ Block: { style: { fontWeight: 'bold' } } }}>{t('solutions.filter_stage')}</LabelSmall>
                    </Cell>
                  </Grid>
                </Block>
                <Block marginTop='1ch'>
                  <Grid gridColumns={4} gridMargins={4}>
                    {filters.map((filter, index) => (
                      <Cell key={index}>
                        {Object.values(filter)[0].map((filterValue, index) => (
                          <Block padding='5px 0' key={filterValue}>
                            <ReDoCheckbox checked={getIsChecked(Object.keys(filter)[0], filterValue)} onChange={onFilter} label={filterValue} />
                          </Block>
                        ))}
                      </Cell>
                    ))}
                  </Grid>
                </Block>
              </div>
            </ReDoBanner>
          </FlexGridItem>

          <FlexGridItem display='flex' flexDirection='column' height='100%'>

            <Block display={['flex', 'flex', 'flex', 'none']} flexDirection='column'>

              <Block marginTop='3ch' display={['unset', 'unset', 'unset', 'none']}>
                <Block height={theme.sizing.scale1400}>
                  <Input
                    onChange={onSearch}
                    startEnhancer={<Search size="24px" />}
                    placeholder={t('solutions.input_search')}
                    overrides={{
                      Root: {
                        style: {
                          height: '100%',
                          backgroundColor: 'white',
                          borderColor: 'transparent !important',
                          borderBottomRightRadius: '0',
                          borderBottomLeftRadius: '0',
                          ':focus-visible': {
                            outline: 'none',
                          },
                        }
                      },
                      StartEnhancer: {
                        style: {
                          backgroundColor: 'white',
                        }
                      },
                      InputContainer: {
                        style: {
                          backgroundColor: 'white',
                        }
                      },
                    }}
                  />
                </Block>
                <Tabs
                  activeKey={activeKey}
                  onChange={({ activeKey }) => setActiveKey(activeKey)}
                  overrides={{
                    Root: {
                      style: ({ $theme }) => ({
                        width: '100%',
                        backgroundColor: 'white',
                        borderBottomLeftRadius: '8px',
                        borderBottomRightRadius: '8px',
                      })
                    },
                    TabBorder: {
                      style: ({ $theme }) => ({
                        display: 'none'
                      })
                    },
                    TabHighlight: {
                      style: ({ $theme }) => ({
                        height: '2px',
                        borderRadius: '25px',
                      })
                    },
                    TabList: {
                      style: ({ $theme }) => ({
                        marginBottom: '3ch',
                        gap: '5%',
                        padding: '0 22px'
                      })
                    },
                  }}
                >
                  {[{ areas: [...filters[0].areas, ...filters[1].areas] }, filters[2], filters[3]].map((filter, index) => (
                    <Tab overrides={{
                      Tab: {
                        style: () => ({
                          backgroundColor: 'transparent',
                          ':hover': {
                            backgroundColor: 'transparent',
                          },
                          paddingBottom: '0.5ch',
                          paddingLeft: '0',
                          paddingRight: '0',
                        })
                      },
                      TabPanel: {
                        style: () => ({
                          padding: '0 22px 22px 22px',
                        })
                      }
                    }} key={index} title={filterNamesTranslated[Object.keys(filter)[0]]}>
                      <Block overrides={{ Block: { style: { columnCount: '2' } } }}>
                        {Object.values(filter)[0].map((filterValue, index) => (
                          <Block padding='5px 0' key={filterValue}>
                            <ReDoCheckbox checked={getIsChecked(Object.keys(filter)[0], filterValue)} onChange={onFilter} label={filterValue} />
                          </Block>
                        ))}
                      </Block>
                    </Tab>
                  ))}

                </Tabs>
              </Block>

            </Block>

            {!!Object.values(chosenFilters).length &&
              <Block marginTop={['4ch', '4ch', '4ch', '50px']} display='flex' alignItems='center'>
                <Block alignItems='center' display='flex' flexWrap='wrap'>
                  {Object.values(chosenFilters)?.map((filter, index) => (
                    filter.map((filterValue, index) => (
                      <Block marginRight='1ch' key={filterValue}>
                        <Tag
                          overrides={{ Root: { style: { borderRadius: '4px !important', fontSize: '13px' } } }}
                          kind={KIND.primary} variant={VARIANT.solid} onActionClick={() => onFilter(null, {
                            checkedFilterName: getChosenFilterFullName(Object.keys(chosenFilters).find(key => chosenFilters[key] === filter), filterValue),
                            checkedFilterValue: false,
                            checkedFilterAPIName: filterValue
                          })} closeable>{getChosenFilterFullName(Object.keys(chosenFilters).find(key => chosenFilters[key] === filter), filterValue)}</Tag>
                      </Block>
                    ))
                  ))}
                  {!!Object.values(chosenFilters)?.flat()?.length &&
                    <Block>
                      <ReDoButton fontSize='14px' color={theme.colors.secondary200} kind='link' size='compact' onClick={resetFilters}>{t('solutions.btn_clear_all')}</ReDoButton>
                    </Block>}
                </Block>
              </Block>
            }

            <Block overflow={['unset', 'unset', 'unset', 'auto']} marginTop={['4ch', '4ch', '4ch', '50px']} paddingBottom={`${NAVBAR_HEIGHT}px`}>
              <Block padding={['0', '0', '0', '0 5ch 0 0']} display={['block', 'block', 'block', 'grid']} gridTemplateColumns={['1fr', '1fr', 'repeat(2, 1fr)', 'repeat(auto-fill, minmax(400px, 1fr))']} gridGap={['4ch', '4ch', '6ch', '8ch']}>
                {!!filteredSolutions?.length &&
                  <Block display={['none', 'none', 'none', 'flex']} gridColumn='1 / -1'
                    alignItems='center' justifyContent='space-between'
                    backgroundColor='white'
                    overrides={{ Block: { style: { borderRadius: '20px', padding: '16px 28px' } } }}
                  >
                    <Block display='flex' flexDirection='column' gridGap='4px'>
                      <div className={css({ fontSize: '14px', fontWeight: '600' })}>
                        {t('solutions.wallet_coins', { balance: wallet.balance ?? 0 })}
                      </div>
                      {wallet.subscription?.nextPayment && !isNaN(new Date(wallet.subscription.nextPayment)) &&
                        <div className={css({ fontSize: '13px', color: theme.colors.contentSecondary })}>
                          {t('solutions.wallet_new_coins', { date: new Date(wallet.subscription.nextPayment).toLocaleDateString() })}
                        </div>
                      }
                    </Block>
                    <Button shape="pill" overrides={{ BaseButton: { style: { border: '1px solid black', color: 'black', backgroundColor: 'transparent', padding: '10px 24px !important', fontWeight: '600', whiteSpace: 'nowrap', ':hover': { backgroundColor: theme.colors.secondary200 } } } }}>
                      {t('solutions.btn_extra_coins')}
                    </Button>
                  </Block>
                }
                {filteredSolutions?.map((solution) => (
                  <Block key={solution.id} height={CARD_HEIGHT} marginBottom={['4ch', '4ch', '6ch', '0']}>
                    <ReDoProjectCard noBanner onFavorite={onFavorite} onClick={() => navigateToSolution(solution)} project={solution} />
                  </Block>
                ))}
              </Block>
            </Block>

          </FlexGridItem>
        </FlexGrid>
      }

    </ >
  );
});

export default SolutionsOverview;
